// dNews / History
//
// Admin audit page - one row per send_job (newsletter / reminder) with
// rolled-up delivery metrics. Filters across the top: kind, status, date
// range, search (subject + recipient email).
//
// Click a row → side drawer slides in with the body preview, attachment
// list, recipient breakdown table, and per-recipient SendGrid status.
//
// Polls every 30s while the page is open so freshly-dispatched sends
// surface without a manual refresh.

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaSearch, FaFilter, FaSyncAlt, FaPaperPlane, FaBell, FaClock,
    FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaEnvelope, FaPaperclip, FaTimes,
    FaFileExcel, FaFilePdf,
} from "react-icons/fa";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiFetch } from "./utils/api";
import { dnAlert } from "./utils/dNews_Alert";
import DNewsSelect from "./utils/dNews_Select";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dNews_Newsletter.css";
import "./dNews_History.css";

const KIND_OPTIONS = [
    { value: "all",        label: "All sends" },
    { value: "newsletter", label: "Newsletter" },
    { value: "reminder",   label: "Reminder" },
];
const STATUS_OPTIONS = [
    { value: "",          label: "Any status" },
    { value: "scheduled", label: "Scheduled" },
    { value: "sending",   label: "Sending" },
    { value: "sent",      label: "Sent" },
    { value: "partial",   label: "Partial" },
    { value: "failed",    label: "Failed" },
    { value: "draft",     label: "Draft" },
];

const KIND_ICON = { newsletter: FaPaperPlane, reminder: FaBell };

function statusPillProps(status) {
    switch (status) {
        case "sent":      return { className: "dnews-hist-pill sent",      icon: FaCheckCircle, label: "Sent" };
        case "partial":   return { className: "dnews-hist-pill partial",   icon: FaExclamationTriangle, label: "Partial" };
        case "failed":    return { className: "dnews-hist-pill failed",    icon: FaTimesCircle, label: "Failed" };
        case "scheduled": return { className: "dnews-hist-pill scheduled", icon: FaClock,       label: "Scheduled" };
        case "sending":   return { className: "dnews-hist-pill sending",   icon: FaSyncAlt,     label: "Sending" };
        case "draft":     return { className: "dnews-hist-pill draft",     icon: FaClock,       label: "Draft" };
        default:          return { className: "dnews-hist-pill",           icon: FaClock,       label: status || "-" };
    }
}

export default function DNewsHistory({ navSize }) {
    const navigate = useNavigate();

    // ── List + filters ───────────────────────────────────────────────────
    const [rows,       setRows]      = useState([]);
    const [total,      setTotal]     = useState(0);
    const [page,       setPage]      = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading,    setLoading]   = useState(true);

    const [kind,   setKind]   = useState("all");
    const [status, setStatus] = useState("");
    const [from,   setFrom]   = useState("");
    const [to,     setTo]     = useState("");
    const [search, setSearch] = useState("");

    // ── Export state - "excel" | "pdf" | null ─────────────────────────────
    const [exporting, setExporting] = useState(null);

    // ── Drawer state ─────────────────────────────────────────────────────
    const [drawerJobId, setDrawerJobId] = useState(null);
    const [drawerData,  setDrawerData]  = useState(null);
    const [drawerRecips, setDrawerRecips] = useState([]);
    const [drawerLoading, setDrawerLoading] = useState(false);

    const fetchList = useCallback(async () => {
        setLoading(true);
        const params = new URLSearchParams({
            kind, status, from, to, search, page: String(page),
        });
        try {
            const res = await apiFetch(`/api/dnews/history/list?${params.toString()}`, { credentials: "include" });
            if (res.status === 401 || res.status === 403) { navigate("/login"); return; }
            const data = await res.json();
            setRows(data.rows || []);
            setTotal(data.total || 0);
            setTotalPages(data.total_pages || 1);
        } catch { /* leave previous data on network blip */ }
        finally { setLoading(false); }
    }, [kind, status, from, to, search, page, navigate]);

    useEffect(() => { fetchList(); }, [fetchList]);

    // Refresh every 30s so in-flight delivery events appear without manual
    // F5. 30s is well under the SendGrid webhook latency budget and still
    // light on the DB.
    useEffect(() => {
        const id = setInterval(fetchList, 30000);
        return () => clearInterval(id);
    }, [fetchList]);

    // Reset to page 1 when any filter changes (otherwise we might be on
    // page 5 of a filter that only returns 2 pages).
    useEffect(() => { setPage(1); }, [kind, status, from, to, search]);

    // ── Fetch all rows (page=1 with a huge page_size) for export. The
    //    backend lists newest-first; we pass the same filters the UI has.
    //    Returns up to 5000 rows - if more, the user should narrow filters.
    const fetchAllRows = async () => {
        const params = new URLSearchParams({
            kind, status, from, to, search,
            page: "1",
            page_size: "5000",
        });
        const res = await apiFetch(`/api/dnews/history/list?${params.toString()}`, { credentials: "include" });
        if (!res.ok) throw new Error("Could not fetch rows for export");
        const data = await res.json();
        return { rows: data.rows || [], total: data.total || 0 };
    };

    // Tiny date formatter for export columns - the IST stamp the UI shows.
    const fmtTs = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "-";

    // ── Excel export - matches dSpr Email Logs visual language exactly.
    //    Navy header band, orange period in "Dolluz Corp.", slate meta
    //    block, navy table header, zebra rows, status colours. Same
    //    palette as src/backend_routes/dSpr_Reports_server.js.
    const exportExcel = async () => {
        setExporting("excel");
        try {
            const data = await fetchAllRows();
            const wb = new ExcelJS.Workbook();
            wb.creator = "Dolluz Corp · dNews";
            wb.created = new Date();
            const ws = wb.addWorksheet("dNews History");

            const XL_NAVY   = "FF0D1B2A";
            const XL_ORANGE = "FFE8520A";
            const XL_SUB    = "FFAABBCC";
            const XL_SLATE  = "FF64748B";
            const XL_LINE   = "FFE2E8F0";
            const XL_ZEBRA  = "FFFAFBFD";

            ws.columns = [
                { key: "sent_at",    width: 22 },
                { key: "kind",       width: 14 },
                { key: "target",     width: 18 },
                { key: "subject",    width: 50 },
                { key: "recipients", width: 12 },
                { key: "delivered",  width: 12 },
                { key: "bounced",    width: 10 },
                { key: "status",     width: 14 },
                { key: "created_by", width: 22 },
            ];

            // Navy header band
            const brandRow = ws.addRow([]);
            ws.mergeCells(brandRow.number, 1, brandRow.number, 9);
            const brandCell = brandRow.getCell(1);
            brandCell.value = {
                richText: [
                    { text: "Dolluz Corp",   font: { bold: true, color: { argb: "FFFFFFFF" }, size: 16, name: "Calibri" } },
                    { text: ".",             font: { bold: true, color: { argb: XL_ORANGE }, size: 16, name: "Calibri" } },
                    { text: "    dNews",     font: { bold: true, color: { argb: XL_ORANGE }, size: 16, name: "Calibri" } },
                ],
            };
            brandCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
            brandCell.alignment = { vertical: "middle", indent: 1 };
            brandRow.height = 28;

            const subRow = ws.addRow(["Send history - newsletter + reminder audit trail"]);
            ws.mergeCells(subRow.number, 1, subRow.number, 9);
            const subCell = subRow.getCell(1);
            subCell.font = { color: { argb: XL_SUB }, size: 10, name: "Calibri" };
            subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
            subCell.alignment = { vertical: "middle", indent: 1 };
            subRow.height = 18;

            ws.addRow([]);

            // Build the filter summary ONLY from non-default fields so the
            // export reads clean ("Generated + Rows") when no filter is set.
            const filterBits = [];
            if (kind && kind !== "all") filterBits.push(`kind=${kind}`);
            if (status)                 filterBits.push(`status=${status}`);
            if (from || to)             filterBits.push(`range=${from || "…"} to ${to || "…"}`);
            if (search)                 filterBits.push(`q="${search}"`);

            const metaRows = [
                ...(filterBits.length ? [["Filter:", filterBits.join(" · ")]] : []),
                ["Generated:", fmtTs(new Date().toISOString()) + " IST"],
                ["Rows:",      String(data.rows.length)],
            ];
            metaRows.forEach(([k, v]) => {
                const row = ws.addRow([k, v]);
                row.getCell(1).font = { color: { argb: XL_SLATE }, size: 10 };
                row.getCell(2).font = { bold: true, color: { argb: XL_NAVY }, size: 10 };
            });
            ws.addRow([]);

            // Table header
            const hdrRow = ws.addRow([
                "Sent At (IST)", "Kind", "Audience", "Subject",
                "Recipients", "Delivered", "Bounced", "Status", "Created By",
            ]);
            hdrRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10.5, name: "Calibri" };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_NAVY } };
                cell.alignment = { vertical: "middle", indent: 1 };
                cell.border = { bottom: { style: "thin", color: { argb: XL_LINE } } };
            });
            hdrRow.height = 22;

            data.rows.forEach((r, i) => {
                const row = ws.addRow({
                    sent_at:    fmtTs(r.sent_at || r.created_at),
                    kind:       r.kind,
                    target:     r.target_type,
                    subject:    r.subject || "",
                    recipients: r.recipient_count || 0,
                    delivered:  r.delivered || 0,
                    bounced:    (r.bounced || 0) + (r.failed_log || 0),
                    status:     r.status,
                    created_by: r.created_by_name || "-",
                });
                row.eachCell((cell, col) => {
                    cell.font = { color: { argb: XL_NAVY }, size: 10, name: "Calibri" };
                    if (i % 2 === 1) {
                        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: XL_ZEBRA } };
                    }
                    cell.alignment = { vertical: "top", wrapText: col === 4 };
                    cell.border = { bottom: { style: "hair", color: { argb: XL_LINE } } };
                });
                const st = String(r.status || "").toLowerCase();
                if (st === "failed" || st === "partial") row.getCell("status").font = { bold: true, color: { argb: "FFB91C1C" }, size: 10 };
                else if (st === "sent")                  row.getCell("status").font = { bold: true, color: { argb: "FF15803D" }, size: 10 };
                else if (st === "scheduled")             row.getCell("status").font = { bold: true, color: { argb: XL_ORANGE }, size: 10 };
            });

            const buf = await wb.xlsx.writeBuffer();
            const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `dnews-history-${(from || "all")}-to-${(to || "now")}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            dnAlert.success(`Exported ${data.rows.length} row${data.rows.length === 1 ? "" : "s"}`);
        } catch (e) {
            dnAlert.error("Excel export failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    // ── PDF export - same theme: navy band, orange period, slate meta.
    const exportPDF = async () => {
        setExporting("pdf");
        try {
            const data = await fetchAllRows();
            const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });

            const NAVY       = [13, 27, 42];
            const ORANGE     = [232, 82, 10];
            const HEADER_SUB = [170, 187, 204];
            const SLATE      = [100, 116, 139];
            const ZEBRA      = [248, 250, 252];
            const pageWidth  = doc.internal.pageSize.getWidth();

            // Navy header band
            doc.setFillColor(NAVY[0], NAVY[1], NAVY[2]);
            doc.rect(0, 0, pageWidth, 60, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(18);
            doc.setTextColor(255, 255, 255);
            doc.text("Dolluz Corp", 28, 32);
            const wDol = doc.getTextWidth("Dolluz Corp");
            doc.setTextColor(ORANGE[0], ORANGE[1], ORANGE[2]);
            doc.text(".", 28 + wDol, 32);
            const wDot = doc.getTextWidth(".");
            doc.text("    dNews", 28 + wDol + wDot, 32);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(HEADER_SUB[0], HEADER_SUB[1], HEADER_SUB[2]);
            doc.text("Send history - newsletter + reminder audit trail", 28, 48);

            // Title - if a date range was set we include it, otherwise just
            // "Send History". The Unicode arrow rendered as "!'" in jsPDF's
            // default helvetica (no glyph), so the title is plain text only.
            const hasRange = !!(from || to);
            const titleRange = hasRange ? ` (${from || "earliest"} - ${to || "now"})` : "";
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
            doc.text(`Send History${titleRange}`, 28, 84);

            // Meta line - only include filter context that's actually set.
            const metaBits = [
                `Generated: ${fmtTs(new Date().toISOString())}`,
                `${data.rows.length} row${data.rows.length === 1 ? "" : "s"}`,
            ];
            if (kind && kind !== "all") metaBits.push(`kind=${kind}`);
            if (status)                 metaBits.push(`status=${status}`);
            if (search)                 metaBits.push(`q="${search}"`);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
            doc.text(metaBits.join("    ·    "), 28, 100);

            autoTable(doc, {
                startY: 116,
                head: [["Sent At (IST)", "Kind", "Audience", "Subject", "To", "Delivered", "Bounced", "Status", "Created By"]],
                body: data.rows.map(r => [
                    fmtTs(r.sent_at || r.created_at),
                    r.kind,
                    r.target_type,
                    (r.subject || "").substring(0, 70),
                    String(r.recipient_count || 0),
                    String(r.delivered || 0),
                    String((r.bounced || 0) + (r.failed_log || 0)),
                    r.status,
                    r.created_by_name || "-",
                ]),
                styles: { fontSize: 8.5, cellPadding: 5, textColor: [13, 27, 42], lineColor: [226, 232, 240], lineWidth: 0.5 },
                headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", fontSize: 9 },
                alternateRowStyles: { fillColor: ZEBRA },
                columnStyles: {
                    0: { cellWidth: 95 },
                    1: { cellWidth: 60 },
                    2: { cellWidth: 80 },
                    3: { cellWidth: "auto" },
                    4: { cellWidth: 50, halign: "right" },
                    5: { cellWidth: 60, halign: "right" },
                    6: { cellWidth: 55, halign: "right" },
                    7: { cellWidth: 60 },
                    8: { cellWidth: 100 },
                },
                didDrawPage: (hookData) => {
                    const ph = doc.internal.pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.setTextColor(SLATE[0], SLATE[1], SLATE[2]);
                    doc.text(
                        `Dolluz Corp · dNews · Page ${hookData.pageNumber}`,
                        28, ph - 18
                    );
                },
            });
            doc.save(`dnews-history-${(from || "all")}-to-${(to || "now")}.pdf`);
            dnAlert.success(`Exported ${data.rows.length} row${data.rows.length === 1 ? "" : "s"}`);
        } catch (e) {
            dnAlert.error("PDF export failed", e.message);
        } finally {
            setExporting(null);
        }
    };

    const openDrawer = async (jobId) => {
        setDrawerJobId(jobId);
        setDrawerLoading(true);
        setDrawerData(null);
        setDrawerRecips([]);
        try {
            const [dRes, rRes] = await Promise.all([
                apiFetch(`/api/dnews/history/${jobId}`,             { credentials: "include" }),
                apiFetch(`/api/dnews/history/${jobId}/recipients`,  { credentials: "include" }),
            ]);
            setDrawerData(await dRes.json());
            setDrawerRecips(await rRes.json());
        } catch { /* tolerated */ }
        finally { setDrawerLoading(false); }
    };
    const closeDrawer = () => { setDrawerJobId(null); setDrawerData(null); setDrawerRecips([]); };

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        dNews - History
                    </h2>
                    <p className="dspr-subtitle">Every newsletter and reminder you've sent or scheduled. Click any row for body preview, attachments, and per-recipient SendGrid status.</p>
                </div>

                <div className="dspr-dashboard-content">
                    {/* ── Filter bar ─────────────────────────────────────── */}
                    <div className="dnews-hist-filters">
                        <div className="dnews-hist-filter" style={{ minWidth: 170 }}>
                            <FaFilter className="dnews-hist-filter-icon" />
                            <DNewsSelect
                                value={kind}
                                onChange={(v) => setKind(v)}
                                options={KIND_OPTIONS}
                            />
                        </div>
                        <div className="dnews-hist-filter" style={{ minWidth: 160 }}>
                            <DNewsSelect
                                value={status}
                                onChange={(v) => setStatus(v)}
                                options={STATUS_OPTIONS}
                            />
                        </div>
                        <div className="dnews-hist-filter dnews-hist-date">
                            <label>From</label>
                            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
                        </div>
                        <div className="dnews-hist-filter dnews-hist-date">
                            <label>To</label>
                            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} onClick={(e) => e.target.showPicker?.()} />
                        </div>
                        <div className="dnews-hist-search">
                            <FaSearch />
                            <input
                                type="text"
                                placeholder="Search subject or recipient email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <button className="dnews-hist-refresh" onClick={fetchList} title="Refresh now">
                            <FaSyncAlt /> Refresh
                        </button>
                        <button
                            className="dnews-hist-export excel"
                            onClick={exportExcel}
                            disabled={!!exporting || loading}
                            title="Export filtered rows to Excel (XLSX)"
                        >
                            <FaFileExcel />
                            {exporting === "excel" ? "Exporting…" : "Excel"}
                        </button>
                        <button
                            className="dnews-hist-export pdf"
                            onClick={exportPDF}
                            disabled={!!exporting || loading}
                            title="Export filtered rows to PDF"
                        >
                            <FaFilePdf />
                            {exporting === "pdf" ? "Exporting…" : "PDF"}
                        </button>
                    </div>

                    {/* ── Counts strip ───────────────────────────────────── */}
                    <div className="dnews-hist-count">
                        {loading
                            ? "Loading…"
                            : `${total} send${total === 1 ? "" : "s"} · page ${page} of ${totalPages}`}
                    </div>

                    {/* ── List ──────────────────────────────────────────── */}
                    <div className="dnews-hist-list">
                        {!loading && rows.length === 0 && (
                            <div className="dnews-hist-empty">No sends match these filters.</div>
                        )}
                        {rows.map(r => {
                            const KindIcon = KIND_ICON[r.kind] || FaPaperPlane;
                            const pill = statusPillProps(r.status);
                            const PIcon = pill.icon;
                            const total = r.recipient_count || 0;
                            const okPct = total > 0 ? Math.round(((r.delivered || 0) / total) * 100) : 0;
                            return (
                                <button
                                    key={r.id}
                                    className={`dnews-hist-row ${drawerJobId === r.id ? "active" : ""}`}
                                    onClick={() => openDrawer(r.id)}
                                >
                                    <div className="dnews-hist-row-icon">
                                        <KindIcon />
                                    </div>
                                    <div className="dnews-hist-row-main">
                                        <div className="dnews-hist-row-subject">{r.subject}</div>
                                        <div className="dnews-hist-row-meta">
                                            <span>{r.kind}</span>
                                            <span>·</span>
                                            <span>{r.target_type}</span>
                                            <span>·</span>
                                            <span>{r.recipient_count} {r.recipient_count === 1 ? "person" : "people"}</span>
                                            {r.created_by_name && <><span>·</span><span>{r.created_by_name}</span></>}
                                        </div>
                                    </div>
                                    <div className="dnews-hist-row-metrics">
                                        {r.status === "scheduled" ? (
                                            <span className="dnews-hist-metric scheduled">
                                                {new Date(r.scheduled_at).toLocaleString()}
                                            </span>
                                        ) : (
                                            <>
                                                <span className="dnews-hist-metric"><strong>{r.delivered || 0}</strong> delivered ({okPct}%)</span>
                                                {(r.bounced > 0 || r.failed_log > 0) && (
                                                    <span className="dnews-hist-metric alert">
                                                        {r.bounced + r.failed_log} failed
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <div className="dnews-hist-row-time">
                                        {new Date(r.sent_at || r.created_at).toLocaleString()}
                                    </div>
                                    <div className={pill.className}>
                                        <PIcon /> {pill.label}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* ── Pagination ────────────────────────────────────── */}
                    {totalPages > 1 && (
                        <div className="dnews-hist-pager">
                            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</button>
                            <span>Page {page} of {totalPages}</span>
                            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Drawer ───────────────────────────────────────────────── */}
            {drawerJobId && (
                <>
                    <div className="dnews-hist-drawer-backdrop" onClick={closeDrawer} />
                    <aside className="dnews-hist-drawer">
                        <div className="dnews-hist-drawer-header">
                            <div>
                                <div className="dnews-hist-drawer-eyebrow">{drawerData?.kind || "..."}</div>
                                <h3>{drawerData?.subject || "Loading…"}</h3>
                            </div>
                            <button className="dnews-hist-drawer-close" onClick={closeDrawer}><FaTimes /></button>
                        </div>

                        {drawerLoading && <div className="dnews-hist-drawer-loading">Loading…</div>}

                        {drawerData && !drawerLoading && (
                            <>
                                <div className="dnews-hist-drawer-meta">
                                    <div><label>Status</label><span>{drawerData.status}</span></div>
                                    <div><label>Sent</label><span>{drawerData.sent_at ? new Date(drawerData.sent_at).toLocaleString() : "-"}</span></div>
                                    <div><label>Created by</label><span>{drawerData.created_by_name || "-"}</span></div>
                                    {drawerData.template_name && <div><label>Template</label><span>{drawerData.template_name}</span></div>}
                                    {drawerData.scheduled_at && <div><label>Scheduled for</label><span>{new Date(drawerData.scheduled_at).toLocaleString()}</span></div>}
                                    <div><label>Recipients</label><span>{drawerData.recipient_count}</span></div>
                                </div>

                                {Array.isArray(drawerData.attachments) && drawerData.attachments.length > 0 && (
                                    <div className="dnews-hist-drawer-block">
                                        <h4><FaPaperclip /> Attachments</h4>
                                        <ul className="dnews-hist-attach-list">
                                            {drawerData.attachments.map(a => (
                                                <li key={a.id}>
                                                    <span>{a.original_name}</span>
                                                    <span className="dnews-hist-attach-meta">{(a.size_bytes / 1024).toFixed(1)} KB</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="dnews-hist-drawer-block">
                                    <h4><FaEnvelope /> Body Preview</h4>
                                    {/* Body is sender-controlled and admin-only; safe to render. */}
                                    <div
                                        className="dnews-hist-body-preview"
                                        dangerouslySetInnerHTML={{ __html: drawerData.body_html || "" }}
                                    />
                                </div>

                                <div className="dnews-hist-drawer-block">
                                    <h4>Recipients ({drawerRecips.length})</h4>
                                    <div className="dnews-hist-recip-table-wrap">
                                        <table className="dnews-hist-recip-table">
                                            <thead>
                                                <tr>
                                                    <th>Email</th>
                                                    <th>Name</th>
                                                    <th>Status</th>
                                                    <th>Last event</th>
                                                    <th>Error</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {drawerRecips.map(r => (
                                                    <tr key={r.id}>
                                                        <td>{r.recipient_email}</td>
                                                        <td>{r.recipient_name || "-"}</td>
                                                        <td><span className={`dnews-hist-recip-status ${r.status}`}>{r.status}</span></td>
                                                        <td>{r.last_event || (r.status === "delivered" ? "delivered" : "-")}</td>
                                                        <td className="dnews-hist-recip-err" title={r.error_message || ""}>
                                                            {r.error_message ? r.error_message.slice(0, 80) : "-"}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {drawerRecips.length === 0 && (
                                                    <tr><td colSpan="5" className="dnews-hist-recip-empty">No recipient log rows yet.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </aside>
                </>
            )}
        </div>
    );
}
